import { useParams, Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDetectLanguage } from "../helpers/language-detector";
import NotFound from "./not-found"
import Social from "../includes/social";
import {usePageTitle} from "../helpers/page-title";

export default function Article() {
    const { t, i18n, ready } = useTranslation();
    const lang = useDetectLanguage();
    const navigate = useNavigate();

    const { id } = useParams();
    const currentId = Number(id);

    const [articleData, setArticleData] = useState(null);
    const [allArticles, setAllArticles] = useState([]);
    const [error, setError] = useState(false);

    // Fetch current article (redirect to NotFound on error)
    useEffect(() => {
        if (!ready || !id) return;

        let cancelled = false;
        setError(false);

        fetch(`/api/${lang}/news/${id}`)
            .then(res => {
                if (!res.ok) throw new Error(`Failed to fetch article: ${res.status}`);
                return res.json();
            })
            .then(data => {
                if (cancelled) return;
                setArticleData(data);
            })
            .catch(err => {
                console.error(err);
                if (cancelled) return;
                setError(true);
            });

        return () => {
            cancelled = true;
        };
    }, [lang, id, i18n, ready]);

    // Redirect when article fetch fails
    useEffect(() => {
        if (!error) return;
        navigate("", { replace: true });
    }, [error, navigate]);

    // Fetch all articles for prev/next logic (optional, does not redirect)
    useEffect(() => {
        if (!ready) return;

        let cancelled = false;

        fetch(`/api/${lang}/news`)
            .then(res => res.json())
            .then(data => {
                if (cancelled) return;
                setAllArticles(Array.isArray(data) ? data : []);
            })
            .catch(err => console.error(err));

        return () => {
            cancelled = true;
        };
    }, [lang, i18n, ready]);

    const { prevId, nextId } = useMemo(() => {
        const ids = allArticles
            .map(a => Number(a?.id))
            .filter(n => Number.isFinite(n))
            .sort((a, b) => a - b);

        const index = ids.indexOf(currentId);

        return {
            prevId: index > 0 ? ids[index - 1] : null,
            nextId: index >= 0 && index < ids.length - 1 ? ids[index + 1] : null,
        };
    }, [allArticles, currentId]);

    // Optional keyboard navigation
    useEffect(() => {
        const handler = (e) => {
            if (e.key === "ArrowLeft" && prevId) navigate(`/news/${prevId}`);
            if (e.key === "ArrowRight" && nextId) navigate(`/news/${nextId}`);
        };

        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [prevId, nextId, navigate]);

    usePageTitle(
        articleData?.title
            ? `${articleData.title} | ${t('title.gaocna')}`
            : t('title.gaocna')
    );

    if (error) return <NotFound />
    if (!articleData) return <p>Loading...</p>;
    return (
        <>
        <Social />
        <div className="news-content">
            <section className="news-info">
                <img src={articleData.thumbnail} alt={articleData.title} />
                <div className="gradient-cover"></div>
                <p className="news-title" style={{ color: "black" }}>
                    {articleData.title} <span>({articleData.published_date})</span>
                </p>
            </section>

            <article
                className="news-article"
                dangerouslySetInnerHTML={{ __html: articleData.content }}
            />

            <div className="article-controls">
                <Link to={prevId ? `/news/${prevId}` : "#"} aria-disabled={!prevId} tabIndex={prevId ? 0 : -1}>
                    <button
                        className={`article-control-btn ${!prevId ? "disabled" : ""}`}
                        id="prev-article"
                        disabled={!prevId}
                        type="button"
                    >
                        <i className="bi bi-caret-left-fill"></i>
                    </button>
                </Link>

                <Link to={nextId ? `/news/${nextId}` : "#"} aria-disabled={!nextId} tabIndex={nextId ? 0 : -1}>
                    <button
                        className={`article-control-btn ${!nextId ? "disabled" : ""}`}
                        id="next-article"
                        disabled={!nextId}
                        type="button"
                    >
                        <i className="bi bi-caret-right-fill"></i>
                    </button>
                </Link>
            </div>
        </div>
        </>
    );
}
